import { SubscriptionModel } from '../model/SubscriptionModel';
import SubscriptionCRUDActions from '../actions/SubscriptionCRUDActions';
import Actions from '../actions/actions';
import { setBusy, setUnBusy } from './common/commonReducers';
import { Subscription, SubscriptionResponse } from '../interfaces/subscription';
import { findIndex } from 'lodash';

const allotments = {
  pro: {
    levels: 0,
    start: 0,
    msgPerLevel: 0,
  },
  unicorn: {
    levels: 0,
    start: 0,
    msgPerLevel: 0,
  },
  team: {
    levels: 0,
    start: 0,
    msgPerLevel: 0,
  },
};

const initialState: Subscription = {
  busy: false,
  token: null,
  coupon: null,
  plans: [],
  plansPrices: {},
  specialDeal: false,
  offerTeam: true,
  accountDetails: undefined,
  priceForNextLevel: { price: 0 },
  currentSubscription: undefined,
  futurePaymentCredits: 0,
  planInfo: undefined,
  plansUserSeats: {
    pro: 0,
    unicorn: 0,
    team: 0,
  },
  planAllotments: { ...allotments },
  annualPlanAllotments: { ...allotments },
};

type SetAccountDetailsAction = ReturnType<
  typeof SubscriptionCRUDActions.setAccountDetailsActionCreator
>;
type SetOveragesEnabledAction = ReturnType<
  typeof SubscriptionCRUDActions.setOveragesEnabledActionCreator
>;

type SetPriceForNextLevelAction = ReturnType<
  typeof SubscriptionCRUDActions.setPriceForNextLevelActionCreator
>;

const setAccountDetails = (
  state: Subscription,
  action: SetAccountDetailsAction,
): Subscription => {
  const { accountDetails } = action;

  return {
    ...state,
    accountDetails,
    busy: false,
  };
};

const setPriceForNextLevel = (
  state: Subscription,
  action: SetPriceForNextLevelAction,
): Subscription => {
  const { priceForNextLevel } = action.data;

  return {
    ...state,
    priceForNextLevel,
  };
};

const clearAccountData = (): Subscription => ({ ...initialState });

type SetPlansAction = ReturnType<
  typeof SubscriptionCRUDActions.setPlansActionCreator
>;

const setPlans = (
  state: Subscription,
  action: SetPlansAction,
): Subscription => {
  const {
    plans,
    planAllotments,
    annualPlanAllotments,
    plansMapping,
    plansPrices,
    specialDeal,
    offerTeam,
    plansUserSeats,
  } = action;

  return {
    ...state,
    plans,
    plansMapping: action.plansMapping || state.plansMapping,
    plansPrices: action.plansPrices || state.plansPrices,
    specialDeal: action.specialDeal || state.specialDeal,
    offerTeam: action.offerTeam || state.offerTeam,
    planAllotments: action.planAllotments || state.planAllotments,
    annualPlanAllotments:
      action.annualPlanAllotments || state.annualPlanAllotments,
    plansUserSeats: action.plansUserSeats || state.plansUserSeats,
  };
};

const setOveragesEnabled = (
  state: Subscription,
  action: SetOveragesEnabledAction,
): Subscription => {
  const { subscriptionId, overagesEnabled } = action.data;
  let subscriptions: SubscriptionModel[];
  if (state.accountDetails === undefined) {
    subscriptions = [];
  } else {
    subscriptions = state.accountDetails.subscriptions || [];
  }
  const subscriptionIndex = subscriptions.findIndex(
    sub => sub.id === subscriptionId,
  );
  if (subscriptionIndex >= 0) {
    const newEntity = {
      [subscriptionIndex]: {
        ...subscriptions[subscriptionIndex],
        overagesEnabled,
      },
    };
    const newEntities = Object.assign([], subscriptions.slice(), newEntity);
    return {
      ...state,
      accountDetails: {
        ...state.accountDetails,
        subscriptions: newEntities,
      } as SubscriptionResponse,
    };
  }
  return state;
};

const setCurrentSubscription = (state: Subscription, action) => {
  const {
    data: { pageId, subscription },
  } = action;
  const { currentSubscription, accountDetails } = state;
  if (accountDetails) {
    const { subscriptions } = accountDetails;

    if (subscriptions) {
      const { id } = subscription;
      const index = findIndex(subscriptions, { id });
      subscriptions.splice(index, 1, subscription);
    }
  }
  const newSubscrObj = {
    ...currentSubscription,
    ...{ [pageId]: subscription },
  };
  return { ...state, currentSubscription: newSubscrObj, accountDetails };
};
const clearCurrentSubscription = (state: Subscription) => {
  return { ...state, currentSubscription: {} };
};

const setCreditsForFuturePayments = (state: Subscription, action) => {
  const {
    data: { futurePaymentCredits },
  } = action;
  return { ...state, futurePaymentCredits };
};

export default function subscription(
  state: Subscription = initialState,
  action,
): Subscription {
  switch (action.type) {
    case SubscriptionCRUDActions.setOveragesEnabledAction:
      return setOveragesEnabled(state, action);
    case SubscriptionCRUDActions.setPriceForNextLevelAction:
      return setPriceForNextLevel(state, action);
    case SubscriptionCRUDActions.busyAction:
      return setBusy(state);
    case SubscriptionCRUDActions.setAccountDetailsAction:
      return setAccountDetails(state, action);
    case SubscriptionCRUDActions.clearAccountDetailsAction:
      return clearAccountData();
    case SubscriptionCRUDActions.setCreditsForFuturePaymentsAction:
      return setCreditsForFuturePayments(state, action);
    case Actions.RESET:
      return initialState;
    case Actions.UNBUSY: {
      return setUnBusy(state);
    }
    case SubscriptionCRUDActions.planInfoAction:
      return { ...state, planInfo: action.plan };
    case SubscriptionCRUDActions.setPlansAction:
      return setPlans(state, action);
    case SubscriptionCRUDActions.unbusyAction: {
      return setUnBusy(state);
    }
    case SubscriptionCRUDActions.setCurrentSubscriptionAction: {
      return setCurrentSubscription(state, action);
    }
    case SubscriptionCRUDActions.clearCurrentSubscriptionAction: {
      return clearCurrentSubscription(state);
    }
    default:
      return state;
  }
}
